import { DocumentNode } from '@completion/models';

export interface ParsedLinkContent {
  displayText?: string;
  mimeType?: string;
  fileName?: string;
  base64?: string; // raw base64 without data: prefix
  embedInAppendix?: boolean;
  secureUrl?: string | null;
  raw?: DocumentNode;
  size?: number;
  addedDate?: string;
  /** Alt text / title for an image attachment */
  altText?: string;
  /** Optional caption shown below an embedded image */
  caption?: string;
  /** Desired display width as a CSS value, e.g. "50%" or "400px". Defaults to "100%". */
  imageWidth?: string;
}

/** Runtime properties that a LINK node may carry beyond the base DocumentNode shape. */
interface LinkNodeExtras {
  secureUrl?: string;
  url?: string;
  mimeType?: string;
  fileName?: string;
  embedInAppendix?: boolean;
  size?: number;
  addedDate?: string;
}

function tryParseJson(raw: unknown): unknown {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
}

export function parseLinkContentFromNode(node: DocumentNode): ParsedLinkContent {
  if (!node) return { raw: node } as ParsedLinkContent;

  const extras = node as DocumentNode & Partial<LinkNodeExtras>;
  const out: ParsedLinkContent = { raw: node };
  // secureUrl may be top-level
  out.secureUrl = extras.secureUrl || extras.url || null;

  // content may be a JSON string or object, or plain display text
  const rawContent = node.content;
  const parsed = tryParseJson(rawContent);

  if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>;
    out.displayText = (obj['displayText'] as string) || (obj['name'] as string) || (obj['title'] as string) || out.displayText;
    out.mimeType = (obj['mimeType'] as string) || (obj['type'] as string) || out.mimeType;
    out.fileName = (obj['fileName'] as string) || (obj['name'] as string) || out.fileName;
    // base64 may be with or without data: prefix
    if (obj['base64']) {
      const b = String(obj['base64'] || '');
      // strip data:...;base64, prefix if present
      const idx = b.indexOf('base64,');
      out.base64 = idx >= 0 ? b.substring(idx + 7) : b;
    }
    if (typeof obj['embedInAppendix'] !== 'undefined') out.embedInAppendix = !!obj['embedInAppendix'];
    if (typeof obj['size'] !== 'undefined') out.size = Number(obj['size']);
    if (typeof obj['addedDate'] !== 'undefined') out.addedDate = String(obj['addedDate']);
    if (typeof obj['altText'] !== 'undefined') out.altText = String(obj['altText']);
    if (typeof obj['caption'] !== 'undefined') out.caption = String(obj['caption']);
    if (typeof obj['imageWidth'] !== 'undefined') out.imageWidth = String(obj['imageWidth']);
  } else if (typeof parsed === 'string') {
    // If content is plain string, treat as display text
    out.displayText = parsed;
  }

  // fallback: some nodes might expose properties directly
  if (!out.displayText) out.displayText = node.content || node.name || '';
  if (!out.mimeType) out.mimeType = extras.mimeType || null;
  if (!out.fileName) out.fileName = extras.fileName || null;
  if (typeof out.embedInAppendix === 'undefined') out.embedInAppendix = !!extras.embedInAppendix;
  if (typeof out.size === 'undefined' && extras.size) out.size = extras.size;
  if (typeof out.addedDate === 'undefined' && extras.addedDate) out.addedDate = extras.addedDate;

  return out;
}

export function toDataUri(base64: string | undefined, mimeType?: string): string | null {
  if (!base64) return null;
  const m = mimeType || 'application/pdf';
  return `data:${m};base64,${base64}`;
}
